"use client";

import React, { useEffect, useId, useLayoutEffect, useRef } from "react";
import { INERT_SLOT } from "../../atoms/inert-slot";
import { Tag } from "../../atoms/tag/tag";
import styles from "./recommender-mini-dock.module.css";
import { withSafeRender } from "../../atoms/safe-render";

export interface MiniDockItem {
  /** The slot's package and add-ons; a new one re-reads the card's look. */
  packageKey: string;
  tag: string;
  name: string;
  priceDollars: string;
  priceCents: string;
  /** The base card's small print, fees and required add-ons included. Empty, the line is omitted. */
  legal: string;
}

export interface RecommenderMiniDockProps {
  items: MiniDockItem[];
  viewLabel: string;
  /** A new answer is loading: the mini cards dim and stop taking input, like the full cards. */
  stale?: boolean;
  /** The rails' container, its children in item order. Its parent, the results under their heading, is where View lands when it can. */
  railsRef: React.RefObject<HTMLDivElement>;
}

/** Room left between a control scrolled clear of the mini cards and their top edge. */
const FOCUS_CLEARANCE_PX = 8;

/** How far in from the tray's edges the glass bends what is behind it, and how hard. */
const LENS_EDGE_PX = 44;
const LENS_SCALE = 90;
/** Frost on what shows through, so it reads as colour and shape, never as text. */
const LENS_FROST = 7;

/* A displacement map along one axis: full push at the near edge, none past the edge band, full pull at the far edge. */
const lensRamp = (length: number, channel: "red" | "green", far: boolean) => {
  const color = (value: number) => (channel === "red" ? `rgb(${value},0,0)` : `rgb(0,${value},0)`);
  const at = (px: number) => Math.min(1, px / length);
  const stops = [[0, 255], [at(LENS_EDGE_PX * 0.35), 200], [at(LENS_EDGE_PX), 128], [1 - at(LENS_EDGE_PX), 128]];
  if (far) {
    stops.push([1 - at(LENS_EDGE_PX * 0.35), 56], [1, 0]);
  } else {
    stops.push([1, 128]);
  }
  const [x2, y2] = channel === "red" ? [length, 0] : [0, length];
  const gradient = stops.map(([offset, value]) => `<stop offset="${offset}" stop-color="${color(value)}"/>`).join("");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${x2 || 1}" height="${y2 || 1}" preserveAspectRatio="none">` +
    `<linearGradient id="g" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="${x2}" y2="${y2}">${gradient}</linearGradient>` +
    "<rect width=\"100%\" height=\"100%\" fill=\"url(#g)\"/></svg>";
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/* Safari accepts an SVG filter in backdrop-filter and then draws no backdrop at all, so the lens is Chromium-only;
   userAgentData ships in Chromium alone. Elsewhere the stylesheet's frosted glass stays. */
const canBendBackdrop = () => typeof navigator !== "undefined" && "userAgentData" in navigator;

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

const matches = (query: string) => typeof window.matchMedia === "function" && window.matchMedia(query).matches;
const prefersReducedMotion = () => matches("(prefers-reduced-motion: reduce)");
/* The stylesheets' tablet breakpoint: below it the mini cards share a row, whatever the rails do. */
const isWide = () => matches("(min-width: 735px)");
/* Safari before 15.4 throws on the selector; there every focus counts as a keyboard one. */
const focusVisible = (element: Element) => {
  try {
    return element.matches(":focus-visible");
  } catch {
    return true;
  }
};
/* Written only on a change, so a scroll frame with nothing new forces no layout before the reads after it. */
const setVar = (element: HTMLElement, name: string, value: string) => {
  if (element.style.getPropertyValue(name) !== value) {
    element.style.setProperty(name, value);
  }
};

/**
 * Until the recommendations scroll into view, a tray at the bottom of the screen
 * holds a mini card per recommendation; selecting one scrolls to that rail and
 * focuses it. The tray is whole or gone: it slides out once the first rail's top
 * reaches its own, and back when the page scrolls up.
 *
 * Where the rails sit side by side, the tray is glass, as wide as the results, and each
 * mini card as wide as its rail. Where they stack, it is opaque and the mini cards share a row.
 */
function RecommenderMiniDockImpl({ items, viewLabel, stale = false, railsRef }: RecommenderMiniDockProps) {
  const dockRef = useRef<HTMLDivElement>(null);
  const lensRef = useRef<SVGFilterElement>(null);
  const lensId = `mini-dock-lens-${useId().replace(/:/g, "")}`;
  const count = items.length;
  const keys = items.map(item => item.packageKey).join(" ");

  useEffect(() => {
    const dock = dockRef.current;
    /* The client half renders straight into the organism's root, so the parent is the recommender. */
    const area = dock?.parentElement;
    if (!dock || !area || count === 0) {
      return undefined;
    }
    let frame = 0;
    /* The tray's top while shown, for the focus check below. */
    let coverTop = Number.POSITIVE_INFINITY;
    let lensSize = "";
    /* Focus outside the recommender that the tray covers. */
    let obscured: EventTarget | null = null;

    /* Liquid glass where the browser can bend a backdrop through an SVG filter; the stylesheet's frosted glass where it cannot. */
    const bend = (tray: HTMLElement, on: boolean) => {
      const lens = lensRef.current;
      /* Cleared every time it is off: a re-run of this effect starts with no record of a lens the tray still wears. */
      if (!on) {
        lensSize = "";
        tray.style.removeProperty("backdrop-filter");
        return;
      }
      const size = `${tray.offsetWidth}x${tray.offsetHeight}`;
      if (!lens || size === lensSize) {
        return;
      }
      lensSize = size;
      const [width, height] = [tray.offsetWidth, tray.offsetHeight];
      lens.setAttribute("width", String(width));
      lens.setAttribute("height", String(height));
      const [across, down] = [...lens.querySelectorAll("feImage")];
      across.setAttribute("href", lensRamp(width, "red", true));
      down.setAttribute("href", lensRamp(height, "green", false));
      [across, down].forEach(image => {
        image.setAttribute("width", String(width));
        image.setAttribute("height", String(height));
      });
      tray.style.setProperty("backdrop-filter", `url(#${lensId}) saturate(1.5) brightness(1.06)`);
    };

    const update = () => {
      frame = 0;
      const cards = railsRef.current;
      const rails = cards ? [...cards.children] as HTMLElement[] : [];
      const tray = dock.firstElementChild as HTMLElement;
      const height = window.innerHeight;
      const box = area.getBoundingClientRect();
      /* offsetTop says whether the rails share a row as laid out. */
      const beside = rails.length > 0 && isWide() && rails.every(rail => rail.offsetTop === rails[0].offsetTop);
      dock.toggleAttribute("data-beside", beside);
      const boxes = rails.map(rail => rail.getBoundingClientRect());
      if (beside && cards) {
        const results = cards.getBoundingClientRect();
        setVar(tray, "--dock-left", `${results.left}px`);
        setVar(tray, "--dock-width", `${results.width}px`);
        setVar(tray, "--dock-gap", `${boxes[1] ? boxes[1].left - boxes[0].right : 0}px`);
        [...tray.children].forEach((mini, index) => setVar(mini as HTMLElement, "--mini-width", `${boxes[index]?.width || 0}px`));
      }
      bend(tray, beside && canBendBackdrop());
      /* Its resting top, whatever the slide is doing. */
      const rest = height - tray.offsetHeight;
      const gone = boxes.length === 0 || boxes[0].top <= rest;
      /* A mini card with focus keeps the tray until focus leaves, so hiding never drops it. */
      const focused = dock.contains(document.activeElement);
      /* Only once the recommender reaches the tray, so it never covers the section above, whose focus it cannot clear. */
      const shown = ((box.top < rest && box.bottom > 0 && !gone) || focused) && !obscured && !searching(document.activeElement);
      coverTop = shown ? rest : Number.POSITIVE_INFINITY;
      /* Not shown, it slides out and then goes visibility: hidden, which also takes the buttons out of the tab order. */
      dock.toggleAttribute("data-shown", shown);
      dock.toggleAttribute("inert", !shown);
    };
    const schedule = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(update);
      }
    };
    /* The channel search's suggestions open below the field, where the tray sits, and the
       on-screen keyboard leaves no room for both; the tray steps aside while the field has focus. */
    const searching = (element: Element | null) => element instanceof Element && area.contains(element) && element.getAttribute("role") === "combobox";
    /* Focus Not Obscured: a control tabbed to under the tray is scrolled clear of it. */
    const uncover = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      /* A rail focused by View is on its way to the top already. */
      const rail = target.parentElement === railsRef.current;
      if (!(target instanceof Element) || dock.contains(target) || rail) {
        return;
      }
      if (searching(target)) {
        schedule();
        return;
      }
      const cover = coverTop - FOCUS_CLEARANCE_PX;
      const { top, bottom } = target.getBoundingClientRect();
      /* Outside the recommender it may be fixed, like the chat launcher, which no scroll clears; the tray steps aside until focus moves on. */
      if (!area.contains(target)) {
        if (bottom > cover && top < window.innerHeight) {
          obscured = target;
          schedule();
        }
        return;
      }
      if (bottom > cover && focusVisible(target)) {
        window.scrollBy(0, bottom - cover);
      }
    };
    const release = (event: FocusEvent) => {
      if (event.target === obscured) {
        obscured = null;
        schedule();
      } else if (searching(event.target as Element)) {
        schedule();
      }
    };
    /* The ZIP panel, the chips or a taller answer move the first rail without a scroll. */
    const resize = typeof ResizeObserver === "function" ? new ResizeObserver(schedule) : null;
    resize?.observe(area);
    /* A longer legal line or name grows the tray without moving the recommender. */
    resize?.observe(dock.firstElementChild as Element);
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    document.addEventListener("focusin", uncover);
    document.addEventListener("focusout", release);
    dock.addEventListener("focusout", schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      resize?.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("focusin", uncover);
      document.removeEventListener("focusout", release);
      dock.removeEventListener("focusout", schedule);
    };
  }, [count, lensId, railsRef]);

  /* Each mini card wears its own card's look: the variant and the glow, which a product's color can override inline.
     Before paint, so a new answer never shows a frame in the old colour. */
  useIsomorphicLayoutEffect(() => {
    const minis = dockRef.current?.firstElementChild?.children;
    const rails = railsRef.current?.children;
    [...(minis || [])].forEach((element, index) => {
      const mini = element as HTMLElement;
      const card = rails?.[index]?.querySelector("article");
      mini.dataset.variant = card?.dataset.variant || "";
      mini.style.setProperty("--mini-glow", card ? getComputedStyle(card).getPropertyValue("--recommender-card-glow").trim() : "");
    });
  }, [keys, railsRef]);

  const view = (index: number) => {
    const rail = railsRef.current?.children[index] as HTMLElement | undefined;
    if (!rail) {
      return;
    }
    rail.focus({ preventScroll: true });
    /* The results' heading comes along while the chosen rail would still start in the top half of the
       screen under it; a rail stacked further down is scrolled to on its own. */
    const results = railsRef.current?.parentElement;
    const below = results ? rail.getBoundingClientRect().top - results.getBoundingClientRect().top : Number.POSITIVE_INFINITY;
    (results && below < window.innerHeight / 2 ? results : rail).scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "start"
    });
  };

  if (count === 0) {
    return null;
  }

  const label = (item: MiniDockItem) => `${viewLabel} ${[item.tag, item.name].filter(Boolean).join(": ")}, ${[`${item.priceDollars}${item.priceCents}`, item.legal].filter(Boolean).join(" ")}`;

  return (
    <div className={styles.dock} ref={dockRef}>
      <div className={styles.inner} data-stale={stale || undefined} {...(stale ? INERT_SLOT : {})}>
        {/* Keyed by position, so a new answer changes each mini card in place instead of replacing it. */}
        {items.map((item, index) => (
          <button aria-label={label(item)} className={styles.mini} key={index} onClick={() => view(index)} type="button">
            {item.tag && <Tag className={styles.tag} color="yellow">{item.tag}</Tag>}
            <span className={`dds-text dds-body-2 dds-weight-medium ${styles.name}`}>{item.name}</span>
            <span className={styles.row}>
              <span className={styles.price}>
                <span className={styles.amount}>
                  <span className={styles.dollars}>{item.priceDollars}</span>
                  <span className={styles.cents}>{item.priceCents}</span>
                </span>
                {item.legal && <span className={styles.legal}>{item.legal}</span>}
              </span>
              <span className={styles.view}>{viewLabel}</span>
            </span>
          </button>
        ))}
      </div>
      {/* The bottom edge sits on the screen's, so only the top and the sides bend. Red, green and blue bend by a little
          less each, the fringe a lens puts on an edge. */}
      <svg aria-hidden="true" className={styles.lensDefs} focusable="false">
        <filter colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse" id={lensId} primitiveUnits="userSpaceOnUse" ref={lensRef} x="0" y="0">
          <feImage preserveAspectRatio="none" result="across" />
          <feImage preserveAspectRatio="none" result="down" />
          <feComposite in="across" in2="down" k2="1" k3="1" operator="arithmetic" result="map" />
          <feGaussianBlur in="SourceGraphic" result="frosted" stdDeviation={LENS_FROST} />
          {[["red", LENS_SCALE, "1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"],
            ["green", LENS_SCALE - 6, "0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0"],
            ["blue", LENS_SCALE - 12, "0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0"]].map(([channel, scale, matrix]) => (
            <React.Fragment key={channel}>
              <feDisplacementMap in="frosted" in2="map" result={`${channel}-bent`} scale={scale} xChannelSelector="R" yChannelSelector="G" />
              <feColorMatrix in={`${channel}-bent`} result={channel as string} type="matrix" values={matrix as string} />
            </React.Fragment>
          ))}
          <feBlend in="red" in2="green" mode="screen" result="red-green" />
          <feBlend in="red-green" in2="blue" mode="screen" />
        </filter>
      </svg>
    </div>
  );
}
export const RecommenderMiniDock = withSafeRender(RecommenderMiniDockImpl, "RecommenderMiniDock");
